import { workerRegistry } from "@/lib/agents/registry"
import { NodeProps, useUpdateNodeInternals } from '@xyflow/react'
import { Plus, Trash2, UserPlus, ChevronDown } from "lucide-react"
import { InlineHandles, NodeHandlers, WorkerLabeledHandle } from "../handles"
import { useWorker, useWorkerContext } from "../hooks"
import { NodeLayout } from './node'
import { ConditionHandler } from "../condition"
import { MemoizedWorker } from "../memoizedworkers"
import { useState, useCallback, useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import { app } from "@/lib/app"
import { useForceUpdate } from "@/hooks/useforceupdate"
import { useForm, useFieldArray, UseFormReturn } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

const { onboard } = workerRegistry
onboard.icon = UserPlus

const OptionSchema = z.object({
  label: z.string(),
  answer: z.string(),
  action: z.enum(["continue", "reset", "goto"]),
  goto: z.string().optional()
})

const ItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  question: z.string(),
  summarize: z.boolean(),
  options: z.array(OptionSchema)
})

const FormSchema = z.object({
  items: z.array(ItemSchema)
})

type FormData = z.infer<typeof FormSchema>
type OptionData = z.infer<typeof OptionSchema>
type ItemData = z.infer<typeof ItemSchema>

interface OnboardOptionEditorProps {
  form: UseFormReturn<FormData>
  itemIndex: number
  optionIndex: number
  itemNames: string[]
  onDelete: () => void
}

function OnboardOptionEditor({ form, itemIndex, optionIndex, itemNames, onDelete }: OnboardOptionEditorProps) {
  const action = form.watch(`items.${itemIndex}.options.${optionIndex}.action`)

  return (
    <div className="border rounded p-2 space-y-2 bg-gray-50">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">Option {optionIndex + 1}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDelete} type="button">
          <Trash2 className="h-3 w-3 text-red-500" />
        </Button>
      </div>
      <FormField control={form.control} name={`items.${itemIndex}.options.${optionIndex}.label`} render={({ field }) => (
        <FormItem className="space-y-1">
          <FormLabel className="text-xs">Label *</FormLabel>
          <FormControl>
            <Input className="h-8 text-sm" {...field} placeholder="Option label" />
          </FormControl>
        </FormItem>
      )} />
      <FormField control={form.control} name={`items.${itemIndex}.options.${optionIndex}.answer`} render={({ field }) => (
        <FormItem className="space-y-1">
          <FormLabel className="text-xs">Answer</FormLabel>
          <FormControl>
            <Textarea className="text-sm min-h-[60px]" {...field} placeholder="Answer (optional)" />
          </FormControl>
        </FormItem>
      )} />
      <div className="flex gap-2">
        <FormField control={form.control} name={`items.${itemIndex}.options.${optionIndex}.action`} render={({ field }) => (
          <FormItem className="flex-1 space-y-1">
            <FormLabel className="text-xs">Action</FormLabel>
            <Select value={field.value || "continue"} onValueChange={field.onChange}>
              <FormControl><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>
                <SelectItem value="continue">Continue</SelectItem>
                <SelectItem value="reset">Reset</SelectItem>
                <SelectItem value="goto">Go to</SelectItem>
              </SelectContent>
            </Select>
          </FormItem>
        )} />
        {action === "goto" && (
          <FormField control={form.control} name={`items.${itemIndex}.options.${optionIndex}.goto`} render={({ field }) => (
            <FormItem className="flex-1 space-y-1">
              <FormLabel className="text-xs">Go to Item</FormLabel>
              <Select value={field.value || ""} onValueChange={field.onChange}>
                <FormControl><SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select item" /></SelectTrigger></FormControl>
                <SelectContent>
                  {itemNames.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormItem>
          )} />
        )}
      </div>
    </div>
  )
}

interface OnboardItemEditorProps {
  form: UseFormReturn<FormData>
  itemIndex: number
  itemNames: string[]
  onDelete: () => void
  onNameChange: (oldName: string, newName: string, itemId: string) => void
}

function OnboardItemEditor({ form, itemIndex, itemNames, onDelete, onNameChange }: OnboardItemEditorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { fields: optionFields, append: appendOption, remove: removeOption } = useFieldArray({ control: form.control, name: `items.${itemIndex}.options` })

  const item = form.watch(`items.${itemIndex}`)
  const currentItemName = item?.name

  const addOption = useCallback(() => {
    appendOption({ label: "", answer: "", action: "continue" })
  }, [appendOption])

  return <div className="border rounded mb-2">
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{item?.title || `Item ${itemIndex + 1}`}</span>
        </div>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pb-3">
        <div className="space-y-3 pt-2">
          <FormField control={form.control} name={`items.${itemIndex}.name`} render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-xs">Name *</FormLabel>
              <FormControl>
                <Input className="h-8 text-sm" {...field} placeholder="Unique item name" onChange={(e) => {
                  const oldName = field.value
                  field.onChange(e)
                  if (oldName !== e.target.value) onNameChange(oldName, e.target.value, item.id)
                }} />
              </FormControl>
            </FormItem>
          )} />
          <FormField control={form.control} name={`items.${itemIndex}.summarize`} render={({ field }) => (
            <FormItem className="flex items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="text-xs">Summarize</FormLabel>
            </FormItem>
          )} />
          {item?.summarize && (
            <FormField control={form.control} name={`items.${itemIndex}.title`} render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-xs">Title *</FormLabel>
                <FormControl><Input className="h-8 text-sm" {...field} placeholder="Item title" /></FormControl>
              </FormItem>
            )} />
          )}
          <FormField control={form.control} name={`items.${itemIndex}.question`} render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-xs">Question *</FormLabel>
              <FormControl>
                <Textarea className="text-sm min-h-[80px]" {...field} placeholder="Question text" />
              </FormControl>
            </FormItem>
          )} />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <FormLabel className="text-xs">Options</FormLabel>
              <Button variant="link" size="sm" className="h-6 px-0 text-xs" onClick={addOption} type="button">
                <Plus className="h-3 w-3 mr-1" /> Add Option
              </Button>
            </div>
            {optionFields.map((optField, optIndex) => (
              <OnboardOptionEditor
                key={optField.id}
                form={form}
                itemIndex={itemIndex}
                optionIndex={optIndex}
                itemNames={itemNames.filter(n => n !== currentItemName)}
                onDelete={() => removeOption(optIndex)}
              />
            ))}
          </div>
          <div className="pt-2 border-t">
            <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={onDelete} type="button">
              <Trash2 className="h-3 w-3 mr-1" /> Delete Item
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  </div>

}

function Parameters() {
  const { worker } = useWorkerContext<OnboardWorker>()
  const { agent } = app
  const updateNodeInternals = useUpdateNodeInternals()
  const forceUpdate = useForceUpdate()

  const initialItems = useMemo(() => {
    const paramItems = worker.parameters.items || []
    return paramItems.map((item, index) => {
      const existingHandler = Object.values(worker.handles).find(h => !h.system && h.name === item.name)
      return {
        id: existingHandler?.id || `item_${index}`,
        name: item.name || "",
        title: item.title || "",
        question: item.question || "",
        summarize: item.summarize ?? true,
        options: (item.options || []).map(opt => ({
          label: opt.label || "",
          answer: opt.answer || "",
          action: opt.action || "continue",
          goto: opt.goto
        }))
      } as ItemData
    })
  }, [])

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: { items: initialItems }
  })

  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({ control: form.control, name: "items" })

  const items = form.watch("items")
  const itemNames = useMemo(() => items.map(i => i.name).filter(Boolean), [items])

  const syncToWorker = useCallback((data: FormData) => {
    worker.parameters.items = data.items.map(item => ({
      name: item.name,
      title: item.title,
      question: item.question,
      summarize: item.summarize,
      options: item.options.map(opt => ({
        label: opt.label,
        answer: opt.answer,
        action: opt.action,
        goto: opt.goto
      }))
    }))
    worker.updateWorker()
    agent.update()
  }, [worker, agent])

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.items) syncToWorker(value as FormData)
    })
    return () => subscription.unsubscribe()
  }, [form, syncToWorker])

  const addItem = useCallback(() => {
    const newId = worker.createHandlerId()
    const newItem: ItemData = {
      id: newId,
      name: "",
      title: "",
      question: "",
      summarize: true,
      options: []
    }
    worker.addHandler({ id: newId, name: "", title: "", type: "string", direction: "output" })
    updateNodeInternals(worker.id)
    agent.updateWorkers()
    agent.update()
    appendItem(newItem)
    forceUpdate()
  }, [worker, agent, updateNodeInternals, appendItem, forceUpdate])

  const deleteItem = useCallback((index: number) => {
    const item = items[index]
    if (item?.id) worker.deleteHandler(item.id)
    updateNodeInternals(worker.id)
    agent.updateWorkers()
    removeItem(index)
    forceUpdate()
  }, [worker, agent, updateNodeInternals, items, removeItem, forceUpdate])

  const handleNameChange = useCallback((oldName: string, newName: string, itemId: string) => {
    const handler = worker.handles[itemId]
    if (handler) {
      worker.updateHandler(itemId, { name: newName, title: newName })
      updateNodeInternals(worker.id)
      agent.updateWorkers()
    }
  }, [worker, agent, updateNodeInternals])

  const [invalidOptionAnswer, setInvalidOptionAnswer] = useState(worker.parameters.invalidOptionAnswer || "")
  const [disableOnHITL, setDisableOnHITL] = useState(worker.parameters.disableOnHITL ?? false)

  const handleInvalidOptionAnswerChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInvalidOptionAnswer(e.target.value)
    worker.parameters.invalidOptionAnswer = e.target.value
    worker.updateWorker()
    agent.update()
  }, [worker, agent])

  const handleDisableOnHITLChange = useCallback((checked: boolean) => {
    setDisableOnHITL(checked)
    worker.parameters.disableOnHITL = checked
    worker.updateWorker()
    agent.update()
  }, [worker, agent])

  return <Form {...form}>
    <form className="px-2 nodrag w-full grow flex flex-col min-w-12">
      <div className="flex items-center justify-between py-2 mb-2">
        <Label htmlFor="disableOnHITL" className="text-xs">Disable on HITL</Label>
        <Switch id="disableOnHITL" checked={disableOnHITL} onCheckedChange={handleDisableOnHITLChange} />
      </div>
      <FormItem className="space-y-1 mb-4">
        <FormLabel className="text-xs">Invalid Option Answer</FormLabel>
        <FormControl>
          <Textarea className="text-sm min-h-[60px]" value={invalidOptionAnswer} onChange={handleInvalidOptionAnswerChange} placeholder="Message when user selects invalid option" />
        </FormControl>
      </FormItem>
      {itemFields.map((field, index) => (
        <OnboardItemEditor
          key={field.id}
          form={form}
          itemIndex={index}
          itemNames={itemNames}
          onDelete={() => deleteItem(index)}
          onNameChange={handleNameChange}
        />
      ))}
      {/* </ScrollArea> */}
      <div className="py-2">
        <Button variant="outline" size="sm" className="w-full" onClick={addItem} type="button">
          <Plus className="h-4 w-4 mr-2" /> Add Item
        </Button>
      </div>
    </form>
  </Form>

}

export function OnboardNode(props: NodeProps) {
  const worker = useWorker<OnboardWorker>(props.id)

  return <NodeLayout worker={worker} className="flex flex-col h-full">

    <InlineHandles>
      <WorkerLabeledHandle handler={worker.fields.input} />
      <WorkerLabeledHandle handler={worker.fields.output} />
    </InlineHandles>

    <InlineHandles>
      <WorkerLabeledHandle handler={worker.fields.history} />
      <WorkerLabeledHandle handler={worker.fields.summary} />
    </InlineHandles>
    <WorkerLabeledHandle handler={worker.fields.finished} />

    <NodeHandlers worker={worker} />

    <MemoizedWorker>
      <Parameters />
    </MemoizedWorker>

    <ConditionHandler />
  </NodeLayout>

}
